import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import ReactNativeInstall from "../../integrate/_snippets/install-react-native.mdx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2 {...{
      "id": "step-one-add-posthog-to-your-app"
    }}>{`Step one: Add PostHog to your app`}</h2>
    <p>{`Using surveys requires PostHog's React Native SDK version >= `}<a parentName="p" {...{
        "href": "https://github.com/PostHog/posthog-js/releases"
      }}>{`4.5.0`}</a>{`. It's recommended to always use the latest version.`}</p>

    <ReactNativeInstall mdxType="ReactNativeInstall" />
    <h2 {...{
      "id": "step-two-install-surveys-dependencies"
    }}>{`Step two: Install surveys dependencies`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`yarn add react-native-safe-area-context react-native-svg
# or
npm i -s react-native-safe-area-context react-native-svg
`}</code></pre>
    <h2 {...{
      "id": "step-three-add-the-surveys-provider"
    }}>{`Step three: Add the surveys provider`}</h2>
    <p>{`Add `}<inlineCode parentName="p">{`PostHogSurveyProvider`}</inlineCode>{` to your app anywhere inside `}<inlineCode parentName="p">{`PostHogProvider`}</inlineCode>{`. This component fetches surveys. It also acts as the root for where popover surveys are rendered.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`<PostHogProvider /*... your config ...*/>
  <PostHogSurveyProvider>{children}</PostHogSurveyProvider>
</PostHogProvider>
`}</code></pre>
    <p>{`If you're not using the `}<inlineCode parentName="p">{`PostHogProvider`}</inlineCode>{`, add `}<inlineCode parentName="p">{`PostHogSurveyProvider`}</inlineCode>{` to your app anywhere inside your app root component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`<YourAppRoot>
  <PostHogSurveyProvider>
</YourAppRoot>
`}</code></pre>
    <p>{`You can also pass your `}<inlineCode parentName="p">{`client`}</inlineCode>{` instance to the `}<inlineCode parentName="p">{`PostHogSurveyProvider`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`<PostHogSurveyProvider client={posthog}>
`}</code></pre>
    <h2 {...{
      "id": "supported-features"
    }}>{`Supported Features`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Feature`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Support`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Questions`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`All question types`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Multi-question surveys`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Confirmation message`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Feedback button presentation`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Conditional questions`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅  >= v4.5.0`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Customization / Appearance`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Set colors in PostHog dashboard`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅ Or override with your app theme`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Shuffle questions`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`PostHog branding`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Delay popup after screen view`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Position config`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌ Always bottom center`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`HTML content`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Display conditions`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Feature flag & property targeting`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Screen targeting`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Device type targeting`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CSS selector matches`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Survey wait period`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Event triggers`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✅`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Action triggers`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Partial responses`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`❌`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h2>
    <ul>
      <li parentName="ul">{`Update your SDK.`}</li>
      <li parentName="ul">{`Run a clean build if you experience issues.`}</li>
      <li parentName="ul">{`For surveys not shown, be sure to set up the `}<a parentName="li" {...{
          "href": "/docs/surveys/installation#step-three-add-the-surveys-provider"
        }}>{`surveys provider`}</a>{`.`}</li>
      <li parentName="ul">{`If you have enabled surveys using feature flags, the flags are evaluated on the device once the PostHog SDK starts as early as possible. The SDK might be using the cached flags from the previous SDK start. If you have changed the flag or its condition, kill and reopen the app to force a new SDK start at least once.`}
        <ul parentName="li">
          <li parentName="ul">{`This will also happen in production, and you might experience some delay for the new flag/conditions to take effect on your users.`}</li>
          <li parentName="ul">{`Survey feature flag evaluation does not capture `}<inlineCode parentName="li">{`$feature_flag_called`}</inlineCode>{` events, so the `}<inlineCode parentName="li">{`Usage`}</inlineCode>{` tab on the feature flag page won't show anything.`}</li>
        </ul>
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      